import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BookOpen, Building2, CalendarClock, FileStack, FileText, Gauge, IdCard, Network, Package, Settings2, ShieldCheck, Sparkles, Users, Wrench,
} from "lucide-react";
import { EquipmentPanel } from "@/components/assets/EquipmentPanel";
import { CreateHouseholdDialog, MembersPanel } from "@/components/household/Members";
import { ConsumptionPanel } from "@/components/household/ConsumptionPanel";
import { HouseholdSettingsPanel } from "@/components/household/HouseholdSettingsPanel";
import { MaintenancePanel } from "@/components/household/MaintenancePanel";
import { RepairsPanel } from "@/components/household/RepairsPanel";
import { InsurancePanel } from "@/components/household/InsurancePanel";
import { DocumentsPanel } from "@/components/household/DocumentsPanel";
import { HouseholdMapPanel } from "@/components/household/HouseholdMapPanel";
import { DemoBadge } from "@/components/common/DemoBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { ModuleCard } from "@/components/common/ModuleCard";
import { PageHeader } from "@/components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useHousehold } from "@/context/HouseholdContext";

const MODULES = [
  { icon: Building2, title: "Plusieurs logements", text: "Résidence principale, secondaire, location.", brief: 3 },
  { icon: IdCard, title: "CASHMY Passport", text: "Le passeport numérique vivant de chaque équipement.", brief: 3 },
  { icon: FileStack, title: "Lecture automatique des documents", text: "Extraction des factures, adresse email documentaire, Google Drive.", brief: 3 },
  { icon: Wrench, title: "Messagerie multilingue", text: "Traduction automatique des échanges avec les professionnels.", brief: 4 },
  { icon: ShieldCheck, title: "Doublons de couverture", text: "Détection des garanties et assurances qui se recoupent.", brief: 4 },
];

const Upcoming = () => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {MODULES.map((m, i) => <ModuleCard key={m.title} {...m} delay={i * 50} testid={`foyer-module-${i}`} />)}
  </div>
);

const TABS = [
  ["equipements", "Équipements", Package, EquipmentPanel],
  ["consommations", "Consommations", Gauge, ConsumptionPanel],
  ["entretiens", "Entretiens", CalendarClock, MaintenancePanel],
  ["sav", "Réparations", Wrench, RepairsPanel],
  ["assurances", "Assurances", ShieldCheck, InsurancePanel],
  ["cartographie", "Fournisseurs", Network, HouseholdMapPanel],
  ["documents", "Documents", FileText, DocumentsPanel],
  ["membres", "Membres", Users, MembersPanel],
  ["parametres", "Paramètres", Settings2, HouseholdSettingsPanel],
  ["modules", "À venir", Sparkles, Upcoming],
];

const TabStrip = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const center = () => el.querySelector("[data-state=active]")?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
    const obs = new MutationObserver(center);
    obs.observe(el, { subtree: true, attributes: true, attributeFilter: ["data-state"] });
    return () => obs.disconnect();
  }, []);
  return (
    <div className="relative -mx-4 sm:mx-0">
      <TabsList ref={ref} className="no-scrollbar flex h-auto w-full snap-x justify-start gap-1 overflow-x-auto rounded-none bg-transparent px-4 py-1 sm:flex-wrap sm:rounded-2xl sm:bg-muted sm:p-1.5" data-testid="foyer-tabs">
        {TABS.map(([v, label, Icon]) => (
          <TabsTrigger key={v} value={v} data-testid={`foyer-tab-${v}`}
            className="shrink-0 snap-start gap-1.5 rounded-full border border-border bg-card px-3.5 py-2 text-sm data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:border-transparent sm:bg-transparent">
            <Icon className="h-4 w-4" />{label}
          </TabsTrigger>
        ))}
      </TabsList>
      <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background sm:hidden" />
    </div>
  );
};

export default function Foyer() {
  const { active, households, select } = useHousehold();
  const [params, setParams] = useSearchParams();
  const hid = params.get("hid");
  useEffect(() => {
    if (hid && hid !== active?.id && households.some((h) => h.id === hid)) select(hid);
  }, [hid]); // eslint-disable-line react-hooks/exhaustive-deps
  const tab = TABS.some(([v]) => v === params.get("tab")) ? params.get("tab") : "equipements";
  const current = TABS.find(([v]) => v === tab);
  return (
    <div className="space-y-8" data-testid="foyer-page">
      <PageHeader eyebrow="CASHMY Home" title={current && tab !== "equipements" ? `Mon Foyer · ${current[1]}` : "Mon Foyer"} text={active ? active.name : "Organisez vos logements, vos membres et vos biens."}
        actions={<>{active?.is_demo && <DemoBadge />}<CreateHouseholdDialog /></>} />
      {!active ? <EmptyState icon={BookOpen} title="Aucun foyer" text="Créez votre premier foyer pour inviter vos proches." /> : (
        <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })} key={active.id}>
          <div className="md:hidden"><TabStrip /></div>
          {TABS.map(([v, , , Panel]) => <TabsContent key={v} value={v} className="mt-6"><Panel /></TabsContent>)}
        </Tabs>
      )}
    </div>
  );
}
